import {
  AttributeTypeEnum,
  ObjectEnum,
  OperatorsEnum,
} from '@celito.clients/enums';
import {
  useActiveModule,
  useConfigureLayout,
  useEffectWithDependencyMonitor,
  useQueryParams,
} from '@celito.clients/hooks';
import { useRecordNavigation } from '@celito.clients/provider';
import { logger } from '@celito.clients/services';
import { useBreadcrumbs } from '@celito.clients/shared';
import {
  getHomeUrl,
  getULHomePageUrl,
  isMajorVersion,
  isObjectEmpty,
} from '@celito.clients/utils';
import { type AxiosError } from 'axios';
import { useFormEngineContext } from 'libs/form-engine/src/lib/hooks';
import { GridViewProps } from 'libs/shared/src/lib/grid-view-new/src';
import { ColumnData } from 'libs/shared/src/lib/grid-view-new/src/types';
import {
  IFilterView,
  RulesComponentListViewFilter,
} from 'libs/shared/src/lib/rules-component/types/rules-component.types';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ListViewModuleType } from './config/list-view.config';
import uiConfig from './config/ui-config';
import { ListViewContext } from './context';
import { ListViewProps } from './list-view.model';
import ListViewUi from './list-view.ui';
import { getObjectData, getObjectMetadata, getViewMetadata } from './services';
import {
  Datum,
  ErrorData,
  ObjectData,
  ObjectMetadata,
  SortConfig,
  ViewMetadata,
} from './types';
import {
  getFiltersFromQueryParams,
  getFilterViewFromQueryParams,
  getGridColumnDataFromObjectMetaData,
  getGridRowDataFromObjectMetaData,
  setFiltersInQueryParams,
  setFilterViewInQueryParams,
  transformObjectMetadata,
} from './utils';
import { getFiltersToApply } from './utils/create-record-filter';
import { exportFile } from './utils/export-file.util';
import { getDefaultFiltersAndRecordData } from './utils/get-default-filters-and-record-data';
import { getNumberOfFilters } from './utils/number-of-fillters.util';
import {
  getIsSortedAscending,
  getSortOrder,
  isReferenceSortSupported,
} from './utils/sort.util';

interface ListViewControllerProps extends ListViewProps {}

const ListViewController = (props: ListViewControllerProps): JSX.Element => {
  const { configureLayout } = useConfigureLayout();
  const { outboundRelationships } = useFormEngineContext();
  const { getSearchParams, setUrlParams, clearUrlParams, searchParams } =
    useQueryParams();
  const { addBreadcrumb } = useBreadcrumbs();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const isInitialLoadRef = useRef(true);
  const [createRoute, setCreateRoute] = useState<string | null>(null);
  const [error, setError] = useState<ErrorData>({
    isError: false,
    errorMessage: '',
  });
  const { updateCurrentNavItem, previousNavItem } = useRecordNavigation();

  const [rawColumnData, setRawColumnData] = useState<ObjectMetadata | null>(
    null
  );
  const [viewMetadata, setViewMetadata] = useState<ViewMetadata | null>(null);

  const viewData = viewMetadata?.viewDto.view[0];

  const [columnData, setColumnData] = useState<GridViewProps['columns']>([]);
  const [rowData, setRowData] = useState<GridViewProps['items']>([]);
  const [pageTitle, setPageTitle] = useState<string>();
  const abortController = useRef(new AbortController());
  const [extraGridProps, setExtraGridProps] = useState<
    Omit<GridViewProps, 'columns' | 'items'>
  >({
    disableSelection: true,
    disableDefaultSorting: true,
    updateEmptyCells: true,
  });
  // initially set this to the itemsPerRow until the API response
  const [totalItems, setTotalItems] = useState(uiConfig.itemsPerRow);
  const [objectName, setObjectName] = useState('');
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  const queryParamFilters = getFiltersFromQueryParams(getSearchParams);
  const [filters, setFilters] = useState<
    RulesComponentListViewFilter | undefined
  >(queryParamFilters);
  const referenceFilter = rawColumnData
    ? rawColumnData.objectAttributeDefinitions.find(
        (attr) =>
          attr?.dataType === AttributeTypeEnum.Reference &&
          attr?.relationship?.objectName === props.objectName
      )
    : undefined;

  // Check if the view has dynamic relationship fields
  const hasDynamicRelationship = useMemo(() => {
    if (!rawColumnData) return false;

    const attributes = rawColumnData.objectAttributeDefinitions;
    const hasObjectId = attributes.some(
      (attr) =>
        attr?.key === 'objectId' ||
        attr?.columnName === 'objectId' ||
        attr?.name === 'object_id__s'
    );
    const hasObjectName = attributes.some(
      (attr) =>
        attr?.key === 'objectName' ||
        attr?.columnName === 'objectName' ||
        attr?.name === 'object_name__s'
    );
    const hasObjectRecordName = attributes.some(
      (attr) =>
        attr?.key === 'objectRecordName' ||
        attr?.columnName === 'objectRecordName' ||
        attr?.name === 'object_record_name__s'
    );
    const hasObjectRecordLabel = attributes.some(
      (attr) =>
        attr?.key === 'objectRecordLabel' ||
        attr?.columnName === 'objectRecordLabel' ||
        attr?.name === 'object_record_label__s'
    );

    return (
      (hasObjectId || hasObjectName) &&
      (hasObjectRecordName || hasObjectRecordLabel)
    );
  }, [rawColumnData]);
  const isRelatedObject =
    useRecordNavigation().previousNavItem?.isRelatedObject;
  const parentRecordName = useParams().recordName;
  const parentRecordVersion = getSearchParams().version;
  const parentFilter = referenceFilter?.columnName
    ? {
        attribute: referenceFilter?.columnName,
        operator: OperatorsEnum.EQUALS,
        referenceFilters: [
          {
            conditions: {
              all: [
                {
                  all: [
                    {
                      attribute: 'name',
                      operator: 'equals',
                      value: parentRecordName,
                    },
                    ...(parentRecordVersion &&
                    isMajorVersion(parentRecordVersion)
                      ? [
                          {
                            attribute: 'version',
                            operator: 'equals',
                            value: parentRecordVersion,
                          },
                        ]
                      : []),
                  ],
                },
              ],
            },
          },
        ],
      }
    : undefined;
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>();
  const [openWorkFlowModal, setOpenWorkFlowModal] = useState<boolean>(false);
  const { pathname } = useLocation();
  const activeModule = useActiveModule();

  const parentObjectName = searchParams.get('objectname');
  const recordName = searchParams.get('recordname');

  const handleWorkflowModalOpen = () => {
    setOpenWorkFlowModal(!openWorkFlowModal);
  };

  const errorHandler = (_error: unknown) => {
    const error = _error as AxiosError;
    setError({
      isError: true,
      errorMessage: error.message,
    });
  };

  const handleSubRowToggle = useCallback((itemKey: string | number) => {
    setRowData((prevItems) =>
      prevItems.map((item) => {
        if (item.key === itemKey && item.subGridData) {
          return {
            ...item,
            subGridData: {
              ...item.subGridData,
              isSubGridOpen: !item.subGridData.isSubGridOpen,
            },
          };
        }
        return item;
      })
    );
  }, []);

  const updateRecordNavigationItem = async (
    response: ObjectData<Datum> | null
  ) => {
    if (!rawColumnData || !viewMetadata) {
      return;
    }

    if (!response) return;

    const rawData = getGridRowDataFromObjectMetaData(
      rawColumnData,
      response,
      viewMetadata,
      handleSubRowToggle
    );

    const records = rawData.map((item) => {
      const searchParams = new URLSearchParams(window.location.search);
      const brParam = searchParams.get('br');

      let url = item?.onClickNavigateRoute ?? '';
      const hasItemUrlSearchParams =
        new URL(url, window.location.origin).searchParams.toString() !== '';

      if (brParam) {
        url += hasItemUrlSearchParams ? `&br=${brParam}` : `?br=${brParam}`;
      }

      const currentUrl = globalThis.location.pathname;
      const isEditView = currentUrl.includes('edit');
      const isCustomView = currentUrl.includes('customview');

      let modifiedUrl: string;
      if (isEditView) {
        modifiedUrl = currentUrl.split('/edit')[0];
      } else if (isCustomView) {
        modifiedUrl = currentUrl.split('/customview')[0];
      } else {
        modifiedUrl = currentUrl.split('/view')[0];
      }

      const combinedUrl = `${modifiedUrl}${url.replace('..', '')}`;

      const urlBreadcrumb = addBreadcrumb({
        label:
          item?.objectRecordLabel ||
          item?.objectRecordTitle ||
          item?.objectRecordName ||
          item?.label ||
          item?.title ||
          item?.name ||
          item?.courseLabel ||
          item?.courseTitle ||
          item?.courseName ||
          item?.course,
        path: combinedUrl,
      });

      return urlBreadcrumb;
    });

    updateCurrentNavItem({
      totalRecords: response.total,
      pageSize: viewData?.defaultPageSize || uiConfig.itemsPerRow,
      records,
      currentIndex: 0,
    });
  };

  const totalItemsInRow = viewData?.defaultPageSize || uiConfig.itemsPerRow;

  const showShimmeringRows = useCallback(() => {
    // show shimmering rows
    setRowData(uiConfig.getLoadingRowData(totalItemsInRow));
  }, [totalItemsInRow]);

  const isShimmering = uiConfig.getLoadedRowData(rowData).length === 0;

  const shouldNotGetLatestVersion = [
    ListViewModuleType.LIBRARY_CONTROLLED_DOCUMENT,
    ListViewModuleType.LIBRARY_COURSE_VIEW,
    ListViewModuleType.LIBRARY_CURRICULUM_VIEW,
    ListViewModuleType.LIBRARY_QUIZ_VIEW,
  ].includes(props.viewName as ListViewModuleType);

  const selectedFilterView = useMemo(
    () =>
      getFilterViewFromQueryParams(
        viewMetadata?.viewDto.view[0].filterViews,
        getSearchParams
      ),
    [getSearchParams, viewMetadata?.viewDto.view]
  );

  const fetchRowData = useCallback(
    async (
      objName: string,
      pageNumber: number,
      customFilters?: RulesComponentListViewFilter,
      sortConf?: SortConfig,
      overrideFilterView?: RulesComponentListViewFilter,
      signal?: AbortSignal | undefined
    ) => {
      try {
        if (!viewMetadata || !rawColumnData) {
          return;
        }
        showShimmeringRows();
        const {
          defaultFilters,
          recordData: recordDataFilter,
          defaultSortConfig,
        } = getDefaultFiltersAndRecordData(
          viewData,
          rawColumnData,
          getSearchParams
        );

        const filtersToApply = getFiltersToApply(
          selectedFilterView,
          props.recordName,
          props.recordVersion,
          referenceFilter,
          props.objectName,
          viewData?.objectName,
          isRelatedObject ? outboundRelationships : undefined,
          hasDynamicRelationship
        );

        const objectDataOptions = {
          sortConfig: sortConf,
          defaultFilters:
            overrideFilterView ?? filtersToApply ?? defaultFilters,
          filters: customFilters || filters,
          defaultSortConfig,
          recordDataFilter,
        };

        const [pageData, allData = null] = await Promise.all([
          getObjectData(
            objName,
            {
              limit: viewMetadata.viewDto.view[0].defaultPageSize,
              page: pageNumber,
            },
            objectDataOptions,
            shouldNotGetLatestVersion,
            signal
          ),
          ...(previousNavItem?.isRelatedObject && pageNumber === 1
            ? [
                getObjectData(
                  objName,
                  {
                    limit: 350,
                    page: 1,
                  },
                  objectDataOptions,
                  shouldNotGetLatestVersion,
                  signal
                ),
              ]
            : []),
        ]);

        if (!pageData) {
          return;
        }

        const newRowData = getGridRowDataFromObjectMetaData(
          rawColumnData,
          pageData,
          viewMetadata,
          handleSubRowToggle
        );

        setRowData(newRowData);

        updateRecordNavigationItem(allData);

        setExtraGridProps((prev) => ({
          ...prev,
          itemsPerRow: viewMetadata.viewDto.view[0].defaultPageSize,
          totalItems: pageData.total,
        }));
        setTotalItems(pageData.total);
      } catch (e) {
        errorHandler(e);
      }
    },
    [
      filters,
      getSearchParams,
      handleSubRowToggle,
      rawColumnData,
      selectedFilterView?.filtersToBeApplied,
      shouldNotGetLatestVersion,
      showShimmeringRows,
      viewData,
      viewMetadata,
      props.objectName,
      selectedFilterView,
    ]
  );

  const onColumnClick: ColumnData['onColumnClick'] = useCallback(
    (_ev: React.MouseEvent<HTMLElement, MouseEvent>, column: ColumnData) => {
      if (column?.data?.isSortableColumn) {
        setColumnData((prev) =>
          prev.map((c) => {
            if (c.key === column.key) {
              return {
                ...c,
                isSortedAscending: getIsSortedAscending(
                  c.isSorted,
                  c.isSortedAscending
                ),
                isSorted: true,
              };
            }
            return {
              ...c,
              isSorted: false,
            };
          })
        );

        const sortConf: SortConfig = {
          attribute: column.data?.name,
          order: getSortOrder(column.isSorted, column.isSortedAscending),
          ...(isReferenceSortSupported(
            column?.data?.dataType as AttributeTypeEnum
          ) && {
            referencedTableColumnName: column?.data?.columnNameToBePicked,
          }),
        };
        setSortConfig(sortConf);

        fetchRowData(objectName, currentPageNumber, filters, sortConf);
      }
    },
    [currentPageNumber, fetchRowData, filters, objectName]
  );

  const handlePageChange = (selectedPage: number) => {
    const nextPage = selectedPage + 1;
    updateCurrentNavItem({
      totalRecords: totalItems,
      page: nextPage,
    });
    setCurrentPageNumber(nextPage);
    fetchRowData(objectName, nextPage, filters, sortConfig);
  };

  const handleFilterViewChange = (filterView: IFilterView) => {
    setFilterViewInQueryParams(
      filterView,
      viewData?.filterViews,
      setUrlParams,
      clearUrlParams
    );
    setCurrentPageNumber(1);
    // Note: fetchRowData will be called automatically by the useEffectWithDependencyMonitor
    // when searchParamsString changes, preventing duplicate API calls
  };

  const applyFilters = useCallback(
    (newFilters: RulesComponentListViewFilter) => {
      setFilters(newFilters);

      // Set filters in query params
      if (isObjectEmpty(newFilters)) {
        clearUrlParams('filters');
      } else {
        setFiltersInQueryParams(newFilters, setUrlParams);
      }

      setCurrentPageNumber(1);

      fetchRowData(objectName, 1, newFilters, sortConfig);
    },

    [clearUrlParams, fetchRowData, objectName, setUrlParams, sortConfig]
  );

  // A cancel button will be shown if specified in the view metadata
  const onCancelPress = () => {
    // FIXME: cancel route should come from BE
    const isULModule = pathname.includes('lms__a');

    navigate(isULModule ? getULHomePageUrl() : getHomeUrl());
  };

  useEffectWithDependencyMonitor(
    (changedDeps) => {
      if (changedDeps.pathname?.before && changedDeps.pathname?.after) {
        setError({
          isError: false,
          errorMessage: '',
        });

        setSortConfig(undefined);
        setFilters(undefined);
      }
    },
    [pathname],
    ['pathname']
  );

  // Initial loading of row data
  useEffect(() => {
    const controller = new AbortController();

    logger.debug(`[${new Date().toISOString}] FETCHING LIST VIEW DATA`, {
      isInitialLoadRef: isInitialLoadRef,
      isLoading: isLoading,
      isShimmering: isShimmering,
    });

    if (isInitialLoadRef.current && isShimmering && !isLoading) {
      fetchRowData(
        objectName,
        1,
        filters,
        undefined,
        undefined,
        controller.signal
      );
      isInitialLoadRef.current = false;
    }

    return () => {
      controller.abort();
    };
  }, [fetchRowData, filters, isLoading, isShimmering, objectName]);

  const searchParamsString = searchParams.toString();

  const viewName = props.viewName;

  useEffectWithDependencyMonitor(
    (changedDeps) => {
      if (Object.keys(changedDeps).includes('viewName')) {
        return;
      }

      if (!Object.keys(changedDeps).includes('searchParamsString')) {
        return;
      }

      fetchRowData(
        objectName,
        1,
        filters,
        undefined,
        undefined,
        abortController.current.signal
      );
      return () => {
        abortController.current.abort();
        abortController.current = new AbortController();
      };
    },
    [searchParamsString, filters, viewName, objectName],
    ['searchParamsString', 'filters', 'viewName', 'objectName']
  );

  useEffectWithDependencyMonitor(
    (changedDeps) => {
      // Load everything when viewName changes
      if (Object.keys(changedDeps).includes('viewName')) {
        setIsLoading(true);
        isInitialLoadRef.current = true;
        setCurrentPageNumber(1);
        setTotalItems(0);

        getViewMetadata(props.viewName)
          .then((pageMetadata) => {
            const vwData = pageMetadata.viewDto?.view[0];

            setObjectName(vwData.objectName);
            setViewMetadata(pageMetadata);

            if (
              vwData?.allowCreateOfNewRecord &&
              vwData?.createRecordViewName
            ) {
              // If create route present, the add/create button is shown in the header
              // redirects to the provided URL
              setCreateRoute(`create/${vwData.createRecordViewName}`);
            } else {
              setCreateRoute(null);
            }

            getObjectMetadata(vwData.objectName)
              .then((results) => {
                if (!results) {
                  return;
                }

                setRawColumnData(results);
                setPageTitle(
                  !isEmpty(vwData.label)
                    ? vwData.label
                    : pageMetadata.viewDto?.label
                );
                showShimmeringRows();
                setIsLoading(false);
              })
              .catch(errorHandler);
          })
          .catch(
            () => navigate('/*') // This will match the root catch-all route
          );
      }
    },
    [props.viewName, configureLayout, getSearchParams],
    ['viewName', 'configureLayout', 'getSearchParams']
  );

  useEffect(() => {
    const recordName = searchParams.get('recordname');
    const objectName = searchParams.get('objectname');
    if (!props.dontSetHeader && pageTitle && !props.objectName)
      configureLayout({
        pageTitle,
        showBackButton: !!recordName && !!objectName,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams.toString() is added for deep comparison
  }, [searchParamsString, pageTitle]);

  useEffect(() => {
    if (!viewMetadata || !rawColumnData) {
      return;
    }

    const columnDataFromObjectMetaData = getGridColumnDataFromObjectMetaData(
      transformObjectMetadata(rawColumnData),
      viewMetadata,
      onColumnClick
    );

    setColumnData((prev) => {
      const prevSortMap: Record<string, any> = prev.reduce(
        (result, column) => {
          result[column.key] = {
            isSorted: column.isSorted,
            isSortedAscending: column.isSortedAscending,
          };
          return result;
        },
        {} as Record<string, any>
      );
      return columnDataFromObjectMetaData.map((column) => ({
        ...column,
        isSorted: prevSortMap[column.key]?.isSorted,
        isSortedAscending: prevSortMap[column.key]?.isSortedAscending,
      }));
    });
  }, [fetchRowData, objectName, onColumnClick, rawColumnData, viewMetadata]);

  function addParentFilter(filters?: RulesComponentListViewFilter) {
    if (isRelatedObject && parentFilter) {
      if (!filters || isObjectEmpty(filters)) {
        return { conditions: { all: [{ all: [parentFilter] }] } };
      }

      const isParentFilterPresent = filters.conditions?.all[0].all.some(
        (filter) =>
          filter.attribute === parentFilter.attribute &&
          filter.operator === parentFilter.operator &&
          JSON.stringify(
            filter.referenceFilters[0].conditions.all[0].all[0].value
          ) ===
            JSON.stringify(
              parentFilter.referenceFilters[0].conditions.all[0].all[0].value
            )
      );

      if (!isParentFilterPresent) {
        filters.conditions?.all[0].all.push(parentFilter);
      }
    }
    return filters;
  }

  const exportFileHandler = exportFile({
    viewData,
    rawColumnData,
    getSearchParams,
    objectName,
    filters: addParentFilter(filters),
    sortConfig,
    shouldNotGetLatestVersion,
    viewMetadata,
    pageTitle,
    moduleName: activeModule?.systemName,
    viewName: props.viewName,
  });

  return (
    <ListViewContext.Provider
      value={{
        fetchRowData,
        onColumnClick,
        showShimmeringRows,
        currentPageNumber,
        filters,
        sortConfig,
        selectedFilterView,
        handleFilterViewChange,
      }}
    >
      <ListViewUi
        isLoading={isLoading}
        columnData={columnData}
        rowData={rowData}
        extraGridProps={extraGridProps}
        error={error}
        handlePageChange={handlePageChange}
        totalItems={totalItems}
        itemsPerRow={viewData?.defaultPageSize || uiConfig.itemsPerRow}
        createRoute={createRoute}
        applyFilters={applyFilters}
        viewMetadata={viewMetadata}
        numberOfFilters={getNumberOfFilters(filters, isRelatedObject)}
        currentPageNumber={currentPageNumber}
        objectName={objectName}
        exportFile={exportFileHandler}
        onCancelPress={onCancelPress}
        objectMetadata={rawColumnData}
        createRecordViewType={
          viewMetadata?.viewDto?.view[0]?.createRecordViewType
        }
        handleWorkflowModalOpen={handleWorkflowModalOpen}
        openWorkFlowModal={openWorkFlowModal}
        {...props}
      />
    </ListViewContext.Provider>
  );
};

export default ListViewController;
